/**
 * A portfolio's underwriting section: one model per property, per side, and the
 * roll-up to the deal. Backed by /underwriting-workbooks/deals/{deal}/… and the
 * placement routes in backend/app/api/v1/underwriting_property_models.py.
 *
 * A portfolio parent's properties are its child deals. A model is filed on the
 * property's own deal record, so it is parsed, mapped and read exactly like any
 * other workbook; what is added here is which property it belongs to, which
 * side (Sponsor or Antarctica), a label, and which one model per property and
 * side the roll-up reads. A deal that is not a portfolio has no property layer.
 */

import api from '@/services/api'
import { toCamelCase } from '@/utils/transform'
import type { WorkbookRole, WorkbookVersionStatus } from '@/services/underwritingWorkbooks'

/** Sponsor is the `sponsor` role; the client's own underwriting is `underwriter`. */
export type SideRole = 'sponsor' | 'underwriter'

/** What each side is called in the portfolio section until the client profile
 *  answers. The underwriter side is the client's own (AREP for Allegiant); a
 *  screen reads it from the profile (`useSideLabels`), never from a literal -
 *  "Antarctica" was hardcoded here (T-29, 2 Nov 2026: say AREP). */
export const SIDE_LABEL: Record<SideRole, string> = {
  sponsor: 'Sponsor',
  underwriter: 'Lender',
}
export const SIDE_ROLES: readonly SideRole[] = ['sponsor', 'underwriter']

/** Another property whose model on the same side is this very file. */
export type SharedModel = { propertyId: string; name: string; versionId: string; filename: string }

export type PropertyModel = {
  id: string
  loanId: string
  versionNumber: number
  filename: string
  label: string | null
  displayName: string
  role: WorkbookRole
  status: WorkbookVersionStatus
  parseStatus: string
  truncated: boolean
  blockerReasons: string[]
  mappingStatus: string
  mappedCellCount: number
  importedCellCount: number
  sheetCount: number
  uploadedBy: string
  uploadedAt: string | null
  /** Marked as the roll-up basis (by a person, or by adding the model). */
  pinned: boolean
  /** The roll-up reads this one today. */
  inRollup: boolean
  eligible: boolean
  ineligibleReason: string | null
  /** What intake or a person recorded when the workbook arrived (provenance a move keeps). */
  note: string | null
  /**
   * Other properties holding this same file as their model on this side. One file cannot
   * stand for several properties, so the roll-up counts it for none of them.
   */
  sharedWith: SharedModel[]
}

/** What adding a model answers: the model, and whether it was already there. */
export type AddedModel = PropertyModel & { reused: boolean }

export type PortfolioProperty = {
  id: string
  name: string
  location: string | null
  assetClass: string | null
  stage: string | null
  models: PropertyModel[]
  basis: Record<SideRole, string | null>
}

export type ExcludedProperty = { id: string; name: string; location: string | null; reason: string }

export type PortfolioModels = {
  dealId: string
  isPortfolio: boolean
  properties: PortfolioProperty[]
  excluded: ExcludedProperty[]
  /** Filed on the portfolio itself: whole-portfolio models and unassigned intake workbooks. */
  dealModels: PropertyModel[]
}

export type RollupSource = {
  versionId: string | null
  versionNumber: number | null
  filename: string | null
  sheet: string | null
  cell: string | null
  label: string | null
  computed: boolean
  alternatives?: { value: number; sheet: string | null; cell: string | null }[]
}

export type RollupInput = {
  field: string
  label: string
  kind: string
  unit: string | null
  value: number
  source: RollupSource
}

export type RollupContributor = {
  propertyId: string
  name: string
  location: string | null
  value: number
  /** Computed from this property's own legs rather than read from a cell. */
  derived: boolean
  /** Whether this value went into the deal figure. */
  rolled: boolean
  source: RollupSource | null
  inputs: RollupInput[]
}

export type RollupMissing = {
  propertyId: string
  name: string
  location: string | null
  reason: 'no_model' | 'not_eligible' | 'not_stated' | 'duplicate_model'
  detail: string
}

export type RolledFigure = {
  field: string
  label: string
  kind: string
  unit: string | null
  group: string
  method: string
  /** The deal's figure: present only when every property contributes. */
  value: number | null
  /** What the contributing properties give, when not every property does. */
  partialValue: number | null
  complete: boolean
  coverage: { have: number; of: number }
  missing: RollupMissing[]
  contributors: RollupContributor[]
  recordValue: number | null
  delta: { absolute: number; relative: number | null; agrees: boolean } | null
}

export type RollupBasisRow = {
  propertyId: string
  name: string
  location: string | null
  model: {
    id: string
    filename: string
    label: string | null
    displayName: string
    versionNumber: number
    status: string
    mappedCellCount: number
    importedCellCount: number
  } | null
  figuresStated: number
  /** Set when the model is filed but withheld from the roll-up, and why. */
  withheld: { reason: string; detail: string } | null
}

export type RollupSide = {
  role: SideRole
  label: string
  coverage: { have: number; of: number }
  missing: RollupMissing[]
  warnings: string[]
  figures: RolledFigure[]
  basis: RollupBasisRow[]
  /** Models filed on a property whose cells reached no figure. */
  unread: {
    propertyId: string
    name: string
    filename: string
    displayName: string
    mappedCellCount: number
    importedCellCount: number
  }[]
}

export type DealRollup = {
  dealId: string
  isPortfolio: boolean
  properties: { id: string; name: string; location: string | null }[]
  excluded: ExcludedProperty[]
  sides: RollupSide[]
}

export type AddModelOptions = {
  role: SideRole
  label?: string
  /** Make the new model the property's roll-up basis for its side. Default true. */
  useInRollup?: boolean
}

/** What withdrawing a model took off the property record, and what was read back. */
export type WithdrawalEffects = {
  /** Deal figures this model filled that were taken back. */
  retractedFields: string[]
  /** Figures filled again from the sponsor models that remain. */
  refilledFields: string[]
  /** `cash_flows` schedules (annual, monthly, UW grid, sources and uses, narrative) it wrote. */
  retractedSchedules: string[]
  /** Comparables it supplied and rent-roll rows it landed. */
  removedComps: number
  removedRentRollRows: number
}

export type RemovedModel = { id: string; removed: true } & WithdrawalEffects

export const uwPropertyModelsService = {
  /** The deal's properties with their models and each side's basis. */
  properties: (dealId: string) =>
    api
      .get(`/underwriting-workbooks/deals/${dealId}/properties`)
      .then((r) => toCamelCase<PortfolioModels>(r.data)),

  /** One deal-level figure per side, rolled up from the property models. */
  rollup: (dealId: string) =>
    api
      .get(`/underwriting-workbooks/deals/${dealId}/rollup`)
      .then((r) => toCamelCase<DealRollup>(r.data)),

  /** Add a model to a property on one side. */
  add: (dealId: string, propertyId: string, file: File, options: AddModelOptions) => {
    const form = new FormData()
    form.append('file', file)
    form.append('workbook_role', options.role)
    if (options.label) form.append('label', options.label)
    if (options.useInRollup === false) form.append('use_in_rollup', 'false')
    return api
      .post(`/underwriting-workbooks/deals/${dealId}/properties/${propertyId}/models`, form)
      .then((r) => toCamelCase<AddedModel>(r.data))
  },

  /** File an existing model under a property (re-registered from its stored bytes). */
  assign: (
    versionId: string,
    body: { propertyId: string; role?: SideRole; label?: string; useInRollup?: boolean },
  ) =>
    api
      .post(`/underwriting-workbooks/${versionId}/assign`, {
        property_id: body.propertyId,
        workbook_role: body.role ?? null,
        label: body.label ?? null,
        use_in_rollup: body.useInRollup ?? true,
      })
      .then((r) => toCamelCase<PropertyModel & WithdrawalEffects>(r.data)),

  /** Relabel a model (null or blank clears it) and/or set whether it is the roll-up basis. */
  updatePlacement: (versionId: string, body: { label?: string | null; rollupBasis?: boolean }) =>
    api
      .patch(`/underwriting-workbooks/${versionId}/placement`, {
        ...('label' in body ? { label: body.label } : {}),
        ...(body.rollupBasis === undefined ? {} : { rollup_basis: body.rollupBasis }),
      })
      .then((r) => toCamelCase<PropertyModel>(r.data)),

  /** Withdraw a model from the section. It stays on file as evidence. */
  remove: (versionId: string, reason?: string) =>
    api
      .delete(`/underwriting-workbooks/${versionId}`, { data: reason ? { reason } : undefined })
      .then((r) => toCamelCase<RemovedModel>(r.data)),
}

/**
 * Query keys for the portfolio section, so every writer invalidates the same two.
 *
 * Both sit under the deal's workbook-list key, so anything that invalidates that
 * list (an upload on the portfolio itself, a role change, a map proposal) refreshes
 * the section and the roll-up with it, with no change to those writers.
 */
export const UW_PORTFOLIO_KEY = (dealId: string) => ['loan-underwriting-workbooks', dealId, 'portfolio'] as const
export const UW_ROLLUP_KEY = (dealId: string) => ['loan-underwriting-workbooks', dealId, 'rollup'] as const
